import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Seo } from "@/components/Seo";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, ArrowLeft, MailCheck } from "lucide-react";
import logo from "@/assets/dosatronic-logo.png";

const BG = "https://images.unsplash.com/photo-1705708551758-76b153fa536e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwyfHx3YXRlciUyMHRyZWF0bWVudCUyMHBsYW50JTIwbW9kZXJufGVufDB8fHx8MTc4Mzg0MzI1Mnww&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login, register } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // login | register | forgot
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [company, setCompany] = useState("");
  const [country, setCountry] = useState("");
  const [phone, setPhone] = useState("");
  const [sector, setSector] = useState("");
  const [interests, setInterests] = useState("ambas");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email });
        setSent(true);
      } else if (mode === "login") {
        const u = await login(email, password);
        navigate(u?.role === "cliente" ? "/productos" : "/");
      } else {
        await register({ name, email, password, company, country, phone, sector, interests });
        navigate("/productos");
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const switchMode = (m) => { setMode(m); setError(""); setSent(false); };

  const title = mode === "login" ? t("auth.signIn") : mode === "register" ? t("auth.signUp") : t("auth.forgotTitle");
  const subtitle = mode === "login" ? t("auth.loginSubtitle") : mode === "register" ? t("auth.registerSubtitle") : t("auth.forgotSubtitle");

  return (
    <div className="flex min-h-screen">
      <Seo title={t("seo.loginTitle")} description={t("seo.loginDesc")} path="/login" />
      <div className="relative flex w-full flex-col justify-center px-6 py-12 sm:px-12 lg:w-1/2 lg:px-20">
        <div className="absolute right-6 top-6"><LanguageSwitcher /></div>
        <div className="mx-auto w-full max-w-sm">
          <div className="mb-10 inline-flex items-center rounded-md px-2.5 py-2" style={{ backgroundColor: "#ffffff" }}>
            <img src={logo} alt="Dosatronic" className="h-9 w-auto" data-testid="brand-logo" />
          </div>

          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">{title}</h1>
          <p className="mt-1 text-sm text-slate-500">{subtitle}</p>

          {mode === "forgot" && sent ? (
            <div className="mt-8">
              <div data-testid="reset-sent" className="flex items-start gap-3 rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
                <MailCheck className="mt-0.5 h-5 w-5 shrink-0" strokeWidth={1.75} />
                <span>{t("auth.resetSent")}</span>
              </div>
              <button type="button" onClick={() => switchMode("login")} data-testid="back-to-login"
                className="mt-6 flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700">
                <ArrowLeft className="h-4 w-4" /> {t("auth.backToLogin")}
              </button>
            </div>
          ) : (
            <>
              <form onSubmit={submit} className="mt-8 space-y-4">
                {mode === "register" && (
                  <div>
                    <Label htmlFor="name" className="text-xs font-semibold uppercase tracking-wide text-slate-500">{t("common.name")}</Label>
                    <Input id="name" data-testid="register-name" value={name} onChange={(e) => setName(e.target.value)}
                      required className="mt-1.5" placeholder={t("auth.yourName")} />
                  </div>
                )}
                {mode === "register" && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">{t("lead.company")}</Label>
                      <Input data-testid="register-company" value={company} onChange={(e) => setCompany(e.target.value)} required className="mt-1.5" />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">{t("lead.country")}</Label>
                      <Input data-testid="register-country" value={country} onChange={(e) => setCountry(e.target.value)} required className="mt-1.5" />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">{t("lead.phone")}</Label>
                      <Input data-testid="register-phone" value={phone} onChange={(e) => setPhone(e.target.value)} required className="mt-1.5" />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">{t("lead.sector")}</Label>
                      <Input data-testid="register-sector" value={sector} onChange={(e) => setSector(e.target.value)} required className="mt-1.5" />
                    </div>
                  </div>
                )}
                {mode === "register" && (
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">{t("lead.interest")}</Label>
                    <Select value={interests} onValueChange={setInterests}>
                      <SelectTrigger data-testid="register-interests" className="mt-1.5"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="info" data-testid="interest-info">{t("lead.interestInfo")}</SelectItem>
                        <SelectItem value="dosaloggers" data-testid="interest-dosaloggers">{t("lead.interestDosaloggers")}</SelectItem>
                        <SelectItem value="ambas" data-testid="interest-both">{t("lead.interestBoth")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wide text-slate-500">{t("common.email")}</Label>
                  <Input id="email" type="email" data-testid="login-email" value={email}
                    onChange={(e) => setEmail(e.target.value)} required className="mt-1.5" placeholder="admin@dosatronic.com" />
                </div>
                {mode !== "forgot" && (
                  <div>
                    <div className="flex items-center justify-between">
                      <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wide text-slate-500">{t("common.password")}</Label>
                      {mode === "login" && (
                        <button type="button" onClick={() => switchMode("forgot")} data-testid="forgot-password-link"
                          className="text-xs font-medium text-blue-600 hover:text-blue-700">
                          {t("auth.forgotPassword")}
                        </button>
                      )}
                    </div>
                    <Input id="password" type="password" data-testid="login-password" value={password}
                      onChange={(e) => setPassword(e.target.value)} required className="mt-1.5" placeholder="••••••••" />
                  </div>
                )}

                {error && (
                  <div data-testid="login-error" className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                    {error}
                  </div>
                )}

                <Button type="submit" disabled={busy} data-testid="login-submit"
                  className="w-full bg-blue-600 text-white hover:bg-blue-700">
                  {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {mode === "login" ? t("auth.enter") : mode === "register" ? t("auth.register") : t("auth.sendResetLink")}
                </Button>
              </form>

              {mode === "forgot" ? (
                <button type="button" onClick={() => switchMode("login")} data-testid="back-to-login"
                  className="mt-6 flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700">
                  <ArrowLeft className="h-4 w-4" /> {t("auth.backToLogin")}
                </button>
              ) : (
                <p className="mt-6 text-center text-sm text-slate-500">
                  {mode === "login" ? t("auth.noAccount") : t("auth.haveAccount")}
                  <button type="button" data-testid="toggle-mode"
                    onClick={() => switchMode(mode === "login" ? "register" : "login")}
                    className="ml-1 font-medium text-blue-600 hover:text-blue-700">
                    {mode === "login" ? t("auth.createOne") : t("auth.signInLink")}
                  </button>
                </p>
              )}
            </>
          )}
        </div>

        <div className="mx-auto mt-12 w-full max-w-md border-t border-slate-100 pt-8" data-testid="seo-content">
          <h2 className="text-base font-semibold text-slate-800">{t("seo.heading")}</h2>
          <p className="mt-3 text-sm leading-relaxed text-slate-500">{t("seo.p1")}</p>
          <p className="mt-3 text-sm leading-relaxed text-slate-500">{t("seo.p2")}</p>
          <p className="mt-3 text-sm leading-relaxed text-slate-500">{t("seo.p3")}</p>
          <p className="mt-3 text-sm leading-relaxed text-slate-500">{t("seo.p4")}</p>
          <ul className="mt-4 space-y-1.5 text-sm text-slate-500">
            <li>· {t("seo.f1")}</li>
            <li>· {t("seo.f2")}</li>
            <li>· {t("seo.f3")}</li>
            <li>· {t("seo.f4")}</li>
            <li>· {t("seo.f5")}</li>
            <li>· {t("seo.f6")}</li>
            <li>· {t("seo.f7")}</li>
          </ul>
          <p className="mt-6 text-xs text-slate-400">
            <Link to="/privacy" className="font-medium text-blue-600 hover:text-blue-700" data-testid="login-privacy-link">
              {t("cookies.policyLink")}
            </Link>
            <span className="mx-2 text-slate-300">·</span>
            <Link to="/sobre" className="font-medium text-blue-600 hover:text-blue-700" data-testid="login-about-link">
              {t("products.about")}
            </Link>
          </p>
        </div>
      </div>

      <div className="relative hidden lg:block lg:w-1/2">
        <img src={BG} alt="Water treatment plant" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/90 via-slate-900/80 to-blue-800/80" />
        <div className="relative flex h-full flex-col justify-end p-12 text-white">
          <h2 className="max-w-md text-3xl font-semibold leading-tight tracking-tight">{t("auth.heroTitle")}</h2>
          <p className="mt-3 max-w-md text-sm text-blue-100">{t("auth.heroSubtitle")}</p>
        </div>
      </div>
    </div>
  );
}
